import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  PageHeader,
  DetailCard,
  ProductSearchInput,
  BarcodeScannerModal,
} from '../components';
import { useCreateTransfer } from '../hooks/useStockTransfers';
import { useWarehouses } from '../hooks/useWarehouses';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors } from '../constants';

const emptyItem = () => ({
  sku: '',
  productUuid: '',
  productType: 'product',
  quantity: '',
  selectedItem: null,
});

export default function StockTransferCreate() {
  const history = useHistory();
  const businessUuid = useBusinessUuid();
  const createTransfer = useCreateTransfer();
  const { data: warehouses = [] } = useWarehouses();

  const [sourceUuid, setSourceUuid] = useState('');
  const [destUuid, setDestUuid] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState(null);
  const [error, setError] = useState(null);

  const whOptions = useMemo(
    () => warehouses.map((w) => ({ value: w.uuid, label: w.name })),
    [warehouses]
  );

  const updateItem = (idx, updates) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...updates } : it)));

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleItemSelected = (idx, item) =>
    updateItem(idx, {
      sku: item.sku,
      productUuid: item.uuid,
      productType: item.type,
      selectedItem: item,
    });

  const handleBarcodeFound = (item) => {
    if (scanTarget !== null && scanTarget < items.length) {
      handleItemSelected(scanTarget, item);
    } else {
      setItems((prev) => [
        ...prev,
        { ...emptyItem(), sku: item.sku, productUuid: item.uuid, productType: item.type, selectedItem: item },
      ]);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (!destUuid) return setError('Select a destination warehouse.');
    if (sourceUuid && sourceUuid === destUuid)
      return setError('Source and destination must be different.');
    if (items.length === 0) return setError('Add at least one item to transfer.');
    for (let i = 0; i < items.length; i += 1) {
      if (!items[i].sku?.trim()) return setError(`Line ${i + 1}: select a product.`);
      const qty = parseFloat(items[i].quantity);
      if (!Number.isFinite(qty) || qty <= 0)
        return setError(`Line ${i + 1}: quantity must be greater than zero.`);
    }
    try {
      await createTransfer.mutateAsync({
        businessUuid,
        fromWarehouseUuid: sourceUuid || undefined,
        toWarehouseUuid: destUuid,
        notes: notes || undefined,
        items: items.map((i) => ({
          productUuid: i.productUuid || i.sku,
          productType: i.productType,
          sku: i.sku,
          quantity: parseFloat(i.quantity) || 0,
        })),
      });
      history.push('/inventory-management/stock-transfers');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Something went wrong');
    }
    return undefined;
  };

  return (
    <>
      <PageHeader title="New Transfer" backTo="/inventory-management/stock-transfers" />

      {error && (
        <Typography variant="body2" sx={{ color: colors.error, mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <DetailCard title="Transfer">
            <Stack spacing={2}>
              <TextField select label="Source Warehouse" value={sourceUuid} onChange={(e) => setSourceUuid(e.target.value)} fullWidth size="small">
                <MenuItem value="">Select source</MenuItem>
                {whOptions.map((w) => (
                  <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                ))}
              </TextField>
              <TextField select label="Destination Warehouse" value={destUuid} onChange={(e) => setDestUuid(e.target.value)} fullWidth required size="small">
                {whOptions.map((w) => (
                  <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                ))}
              </TextField>
              <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} size="small" />
            </Stack>
          </DetailCard>
        </Grid>

        <Grid item xs={12} md={7}>
          <DetailCard
            title={`Items ${items.length ? `(${items.length})` : ''}`}
            action={
              <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={addItem} sx={{ textTransform: 'none' }}>
                Add item
              </Button>
            }
          >
            {items.length === 0 ? (
              <Typography variant="body2" sx={{ color: colors.gray400, py: 3, textAlign: 'center' }}>
                No items to transfer. Tap "Add item" to begin.
              </Typography>
            ) : (
              items.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{ p: 2, mb: 1.5, borderRadius: 2, border: `1px solid ${colors.border}`, backgroundColor: colors.bg }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: colors.gray600 }}>
                      Item {idx + 1}
                    </Typography>
                    <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <ProductSearchInput
                    value={item.sku}
                    onChangeText={(v) => updateItem(idx, { sku: v, productUuid: '', selectedItem: null })}
                    onItemSelected={(found) => handleItemSelected(idx, found)}
                    productType="product"
                    selectedItem={item.selectedItem}
                    onClear={() => updateItem(idx, { sku: '', productUuid: '', selectedItem: null })}
                    onBarcodeScan={() => {
                      setScanTarget(idx);
                      setScannerOpen(true);
                    }}
                  />
                  <TextField
                    label="Quantity"
                    type="number"
                    required
                    size="small"
                    fullWidth
                    sx={{ mt: 1 }}
                    value={item.quantity}
                    onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                    helperText={item.selectedItem ? `Available: ${item.selectedItem.stock} ${item.selectedItem.unit}` : ' '}
                  />
                </Box>
              ))
            )}
          </DetailCard>

          <Box sx={{ mt: 2.5 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={createTransfer.isPending || items.length === 0}
              sx={{ backgroundColor: colors.primary, textTransform: 'none', fontWeight: 600, py: 1.25 }}
            >
              Create Transfer
            </Button>
          </Box>
        </Grid>
      </Grid>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onItemFound={handleBarcodeFound}
        onBarcodeNotFound={(code) => setError(`No product found for barcode "${code}".`)}
      />
    </>
  );
}
